import type { CSSProperties } from "react";
import type {
  LiveBroadcastProvider,
  LiveBroadcastStream,
} from "@/lib/live-broadcast-types";

export interface LiveProviderBrand {
  provider: LiveBroadcastProvider;
  platformName: "Twitch" | "YouTube";
  liveOnLabel: string;
  ctaLabel: string;
  notificationLabel: string;
  pillExpandLabel: string;
  dismissLabel: string;
  headerButtonLabel: string;
  opensInNewTabCopy: string;
  accentLineStyle: CSSProperties;
  cardShadowClassName: string;
  pillClassName: string;
  primaryButtonClassName: string;
  chipClassName: string;
}

const TWITCH_BRAND: LiveProviderBrand = {
  provider: "twitch",
  platformName: "Twitch",
  liveOnLabel: "Live on Twitch",
  ctaLabel: "Watch on Twitch",
  notificationLabel: "Twitch live notification",
  pillExpandLabel: "Expand Twitch live notification",
  dismissLabel: "Dismiss Twitch live notification",
  headerButtonLabel: "Creators live on Twitch",
  opensInNewTabCopy: "Opens Twitch in a new tab",
  accentLineStyle: {
    background:
      "linear-gradient(90deg, rgba(145,70,255,0) 0%, rgba(145,70,255,0.5) 30%, rgba(145,70,255,0.7) 50%, rgba(145,70,255,0.5) 70%, rgba(145,70,255,0) 100%)",
  },
  cardShadowClassName:
    "shadow-[0_8px_40px_rgba(0,0,0,0.6),0_0_0_1px_rgba(145,70,255,0.06)]",
  pillClassName:
    "shadow-[0_4px_24px_rgba(0,0,0,0.5),0_0_0_1px_rgba(145,70,255,0.08)] hover:border-[#9146FF]/30 hover:shadow-[0_4px_24px_rgba(0,0,0,0.5),0_0_20px_rgba(145,70,255,0.12)]",
  primaryButtonClassName:
    "bg-gradient-to-r from-[#9146FF] to-[#7c3aed] shadow-[0_2px_12px_rgba(145,70,255,0.25)] hover:from-[#a362ff] hover:to-[#8b5cf6] hover:shadow-[0_2px_20px_rgba(145,70,255,0.35)] focus-visible:ring-[#9146FF]/50",
  chipClassName:
    "text-[#c4a3ff] bg-[#9146FF]/12 ring-1 ring-inset ring-[#9146FF]/20",
};

const YOUTUBE_BRAND: LiveProviderBrand = {
  provider: "youtube",
  platformName: "YouTube",
  liveOnLabel: "Live on YouTube",
  ctaLabel: "Watch on YouTube",
  notificationLabel: "YouTube live notification",
  pillExpandLabel: "Expand YouTube live notification",
  dismissLabel: "Dismiss YouTube live notification",
  headerButtonLabel: "Creators live on YouTube",
  opensInNewTabCopy: "Opens YouTube in a new tab",
  accentLineStyle: {
    background:
      "linear-gradient(90deg, rgba(255,0,51,0) 0%, rgba(255,0,51,0.55) 30%, rgba(255,0,51,0.8) 50%, rgba(255,0,51,0.55) 70%, rgba(255,0,51,0) 100%)",
  },
  cardShadowClassName:
    "shadow-[0_8px_40px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,0,51,0.07)]",
  pillClassName:
    "shadow-[0_4px_24px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,0,51,0.09)] hover:border-[#FF0033]/30 hover:shadow-[0_4px_24px_rgba(0,0,0,0.5),0_0_20px_rgba(255,0,51,0.14)]",
  primaryButtonClassName:
    "bg-gradient-to-r from-[#FF0033] to-[#CC0000] shadow-[0_2px_12px_rgba(255,0,51,0.28)] hover:from-[#FF1F4D] hover:to-[#E60000] hover:shadow-[0_2px_20px_rgba(255,0,51,0.4)] focus-visible:ring-[#FF0033]/50",
  chipClassName:
    "text-[#FF6B82] bg-[#FF0033]/12 ring-1 ring-inset ring-[#FF0033]/25",
};

const BRANDS: Record<LiveBroadcastProvider, LiveProviderBrand> = {
  twitch: TWITCH_BRAND,
  youtube: YOUTUBE_BRAND,
};

export function getLiveProviderBrand(
  provider: LiveBroadcastProvider,
): LiveProviderBrand {
  return BRANDS[provider];
}

export interface LiveHeaderCopy {
  title: string;
  ariaLabel: string;
  footer: string;
}

export function resolveLiveHeaderCopy(
  streams: ReadonlyArray<Pick<LiveBroadcastStream, "provider">>,
): LiveHeaderCopy {
  const providers = new Set(streams.map((s) => s.provider));

  if (providers.size === 1) {
    const onlyProvider = providers.values().next().value as LiveBroadcastProvider | undefined;
    if (onlyProvider) {
      const brand = getLiveProviderBrand(onlyProvider);
      return {
        title: brand.liveOnLabel,
        ariaLabel: brand.headerButtonLabel,
        footer: brand.opensInNewTabCopy,
      };
    }
  }

  return {
    title: "Live now",
    ariaLabel: "Creators live now",
    footer: "Opens the selected stream in a new tab",
  };
}
